import { APP_ROUTES } from '../constants';
import { NotificationType, NOTIFICATION_TYPE } from '../interface/notification-log.interface';
import { postFormatTimestamp } from './utility';

/**
 * Notification icon and color style configuration
 */
interface NotificationStyle {
  icon: string;
  iconColor: string;
  bgColor: string;
}

/**
 * Maps notification type to appropriate icon and colors
 * Uses PrimeIcons and Classveew theme colors
 *
 * @param type - Notification type from backend NOTIFICATION_TYPES enum
 * @returns Object containing icon, iconColor, and bgColor for styling
 */
export const getNotificationStyle = (type: NotificationType): NotificationStyle => {
  switch (type) {
    // Event related
    case 'EVENT':
      return {
        icon: 'pi pi-calendar',
        iconColor: 'text-purple-600',
        bgColor: 'bg-purple-100',
      };

    // Message/Communication
    case 'MESSAGE':
      return {
        icon: 'pi pi-envelope',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-100',
      };

    // Social - Like & Comment
    case 'LIKE':
      return {
        icon: 'pi pi-heart-fill',
        iconColor: 'text-red-500',
        bgColor: 'bg-red-100',
      };
    case 'COMMENT':
      return {
        icon: 'pi pi-comment',
        iconColor: 'text-indigo-600',
        bgColor: 'bg-indigo-100',
      };
    case 'POST_CREATED':
      return {
        icon: 'pi pi-file-edit',
        iconColor: 'text-teal-600',
        bgColor: 'bg-teal-100',
      };

    // Attendance related
    case 'ATTENDANCE':
    case 'ATTENDANCE_CHECK_IN':
    case 'ATTENDANCE_CHECK_OUT':
      return {
        icon: 'pi pi-check-circle',
        iconColor: 'text-green-600',
        bgColor: 'bg-green-100',
      };
    case 'ATTENDANCE_ABSENT':
    case 'STUDENT_ATTENDANCE_REMINDER':
      return {
        icon: 'pi pi-times-circle',
        iconColor: 'text-red-600',
        bgColor: 'bg-red-100',
      };
    case 'ATTENDANCE_LATE_CHECK_IN':
    case 'ATTENDANCE_EARLY_CHECK_OUT':
      return {
        icon: 'pi pi-clock',
        iconColor: 'text-orange-600',
        bgColor: 'bg-orange-100',
      };

    // Invoice/Finance related
    case 'INVOICE_REMINDER':
      return {
        icon: 'pi pi-file',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-100',
      };
    case 'INVOICE_OVERDUE_REMINDER':
      return {
        icon: 'pi pi-exclamation-triangle',
        iconColor: 'text-red-600',
        bgColor: 'bg-red-100',
      };

    // Award related
    case 'STUDENT_AWARD':
      return {
        icon: 'pi pi-star-fill',
        iconColor: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
      };

    // Exam related
    case 'EXAM_CREATED':
    case 'EXAM_FOLLOWUP_REMINDER':
      return {
        icon: 'pi pi-book',
        iconColor: 'text-purple-600',
        bgColor: 'bg-purple-100',
      };
    case 'UPCOMING_EXAM':
      return {
        icon: 'pi pi-calendar-plus',
        iconColor: 'text-purple-600',
        bgColor: 'bg-purple-100',
      };
    case 'SCORECARD_RELEASED':
      return {
        icon: 'pi pi-chart-bar',
        iconColor: 'text-green-600',
        bgColor: 'bg-green-100',
      };

    // Scheduled notifications
    case 'UPCOMING_EVENT':
      return {
        icon: 'pi pi-calendar-plus',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-100',
      };
    case 'ACADEMIC_YEAR_REMINDER':
      return {
        icon: 'pi pi-calendar-times',
        iconColor: 'text-orange-600',
        bgColor: 'bg-orange-100',
      };

    // Re-enrollment related styles
    case 'RE_ENROLLMENT_IMMEDIATE_TRANSFER':
    case 'RE_ENROLLMENT_CYCLE_COMPLETED':
    case 'RE_ENROLLMENT_NEW_YEAR_ACTIVE':
      return {
        icon: 'pi pi-refresh',
        iconColor: 'text-purple-600',
        bgColor: 'bg-purple-100',
      };

    // Timetable related (subject assignment/unassignment/reassignment)
    case 'TIMETABLE':
      return {
        icon: 'pi pi-table',
        iconColor: 'text-cyan-600',
        bgColor: 'bg-cyan-100',
      };

    // Classroom assignment related (Primary/Acting teacher assigned/unassigned/role-changed)
    case 'CLASSROOM_ASSIGNMENT':
      return {
        icon: 'pi pi-users',
        iconColor: 'text-sky-600',
        bgColor: 'bg-sky-100',
      };

    // Guardian requested to link a student to a classroom ("My Child" request) - pending staff review
    case 'GUARDIAN_LINK_REQUESTED':
      return {
        icon: 'pi pi-user-plus',
        iconColor: 'text-pink-600',
        bgColor: 'bg-pink-100',
      };

    default:
      return {
        icon: 'pi pi-bell',
        iconColor: 'text-gray-600',
        bgColor: 'bg-gray-100',
      };
  }
};

/**
 * Maps notification type to APP_ROUTES for navigation
 * Routes user to appropriate management screen based on notification type
 *
 * @param type - Notification type from NOTIFICATION_TYPE enum
 * @returns The corresponding route from APP_ROUTES or null if not found
 */
export const mapNotificationTypeToRoute = (type: NotificationType): string | null => {
  switch (type) {
    // Event notifications
    case NOTIFICATION_TYPE.EVENT:
    case NOTIFICATION_TYPE.UPCOMING_EVENT:
      return APP_ROUTES.EVENT_MANAGEMENT;

    // Message/Communication notifications
    case NOTIFICATION_TYPE.MESSAGE:
    case NOTIFICATION_TYPE.COMMENT:
    case NOTIFICATION_TYPE.LIKE:
    case NOTIFICATION_TYPE.POST_CREATED:
      return APP_ROUTES.ADMIN_NEWS_FEED;

    // Attendance notifications
    case NOTIFICATION_TYPE.ATTENDANCE:
    case NOTIFICATION_TYPE.ATTENDANCE_ABSENT:
    case NOTIFICATION_TYPE.ATTENDANCE_CHECK_IN:
    case NOTIFICATION_TYPE.ATTENDANCE_CHECK_OUT:
    case NOTIFICATION_TYPE.ATTENDANCE_LATE_CHECK_IN:
    case NOTIFICATION_TYPE.ATTENDANCE_EARLY_CHECK_OUT:
    case NOTIFICATION_TYPE.STUDENT_ATTENDANCE_REMINDER:
      return APP_ROUTES.ATTENDANCE;

    // Invoice/Finance notifications
    case NOTIFICATION_TYPE.INVOICE_REMINDER:
    case NOTIFICATION_TYPE.INVOICE_OVERDUE_REMINDER:
      return APP_ROUTES.INVOICE_MANAGEMENT;

    // Award notifications
    case NOTIFICATION_TYPE.STUDENT_AWARD:
      return APP_ROUTES.ADMIN_AWARDS;

    // Exam notifications
    case NOTIFICATION_TYPE.EXAM_CREATED:
    case NOTIFICATION_TYPE.EXAM_FOLLOWUP_REMINDER:
    case NOTIFICATION_TYPE.UPCOMING_EXAM:
    case NOTIFICATION_TYPE.SCORECARD_RELEASED:
      return APP_ROUTES.EXAM_MANAGEMENT;

    // Academic Year notifications
    case NOTIFICATION_TYPE.ACADEMIC_YEAR_REMINDER:
      return APP_ROUTES.ACADEMIC_YEAR_MANAGEMENT;

    // Re-enrollment related notifications - route to re-enrollment management
    case NOTIFICATION_TYPE.RE_ENROLLMENT_IMMEDIATE_TRANSFER:
    case NOTIFICATION_TYPE.RE_ENROLLMENT_CYCLE_COMPLETED:
    case NOTIFICATION_TYPE.RE_ENROLLMENT_NEW_YEAR_ACTIVE:
      return APP_ROUTES.RE_ENROLLMENT_MANAGEMENT;

    // Timetable notifications - teacher assigned/unassigned/reassigned from a subject
    case NOTIFICATION_TYPE.TIMETABLE:
      return APP_ROUTES.TIMETABLE_MANAGEMENT;

    default:
      return null;
  }
};

/**
 * @deprecated Use mapNotificationTypeToRoute instead
 * Maps notification screen identifier to APP_ROUTES
 * Kept for backward compatibility
 *
 * @param screen - Notification screen identifier from backend
 * @returns The corresponding route from APP_ROUTES or null if not found
 */
export const mapScreenToRoute = (screen: string): string | null => {
  switch (screen) {
    case 'Event-screen':
      return APP_ROUTES.EVENT_MANAGEMENT;
    case 'Invoice-screen':
      return APP_ROUTES.INVOICE_MANAGEMENT;
    case 'Award-screen':
      return APP_ROUTES.ADMIN_AWARDS;
    case 'Attendance-screen':
      return APP_ROUTES.ATTENDANCE;
    case 'MessageScreen':
      return APP_ROUTES.ADMIN_NEWS_FEED;
    case 'Post-screen':
      return APP_ROUTES.DASHBOARD;
    case 'Exam-screen':
      return APP_ROUTES.EXAM_MANAGEMENT;
    case 'Calendar-screen':
      // Scheduled notifications (events/exams) use calendar screen
      return APP_ROUTES.EVENT_MANAGEMENT;
    case 'AcademicYear-screen':
      return APP_ROUTES.ACADEMIC_YEAR_MANAGEMENT;
    default:
      return null;
  }
};

/**
 * Formats notification timestamp to human-readable relative time
 * Uses existing postFormatTimestamp utility from utility.ts
 *
 * @param createdAt - ISO timestamp string from notification
 * @returns Formatted time string (e.g., 'Just now', "5 mins ago", "Yesterday", "Jan 15, 2025")
 */
export const formatNotificationTime = (createdAt: string): string => {
  return postFormatTimestamp(createdAt);
};
